import { isCmdCtrlClick } from 'app/shared/utils/keyboard';
import { openInNewTab, ROUTES_MAP } from 'app/shared/utils/routes';
import {
  AiAdminTypes,
  SidebarComponentTypes,
  SidebarDefinition,
} from 'app/modules/sidebar/models';
import { toggleSidebar } from 'app/modules/sidebar/slice';
import { useDispatch, useSelector } from 'react-redux';
import { MouseEvent, useCallback } from 'react';
import { AIAdminTracking } from 'app/modules/aiAdmin/components/aiAdminTracking/models';
import { computeDlHashIdForCgdo } from 'app/shared/utils/dlHashId';
import {
  selectDLEnabledForEntityRiskScore,
  selectDLEnabledForInstrumentDetail,
} from 'app/shared/featureFlags/selectors';
import { selectAgentOrgID } from 'app/modules/session/selectors';

export const useToggleSidebar = () => {
  const dispatch = useDispatch();
  return useCallback(
    (
      route: string | undefined,
      sidebarDefinition: SidebarDefinition,
      e: MouseEvent,
    ) => {
      if (isCmdCtrlClick(e) && route) {
        openInNewTab(route);
      } else {
        dispatch(
          toggleSidebar({
            ...sidebarDefinition,
            coordinates: {
              x: e.clientX,
              y: e.clientY,
            },
          }),
        );
      }
    },
    [dispatch],
  );
};

export const useToggleEntitySidebar = () => {
  const toggleSidebarCallback = useToggleSidebar();
  const useCacheEndpoint = useSelector(selectDLEnabledForEntityRiskScore);
  const orgId = useSelector(selectAgentOrgID);

  return useCallback(
    (id: number | string, e: MouseEvent, options?: { externalId?: string }) => {
      const { externalId } = options ?? {};
      const routeId =
        externalId && useCacheEndpoint
          ? computeDlHashIdForCgdo(orgId, externalId)
          : String(id);

      toggleSidebarCallback(
        ROUTES_MAP.entitiesId.path.replace(':id', routeId),
        {
          type: SidebarComponentTypes.ENTITY,
          data: { id, ...(externalId && { externalId }) },
        },
        e,
      );
    },
    [toggleSidebarCallback, useCacheEndpoint, orgId],
  );
};

export const useToggleInstrumentSidebar = () => {
  const toggleSidebarCallback = useToggleSidebar();
  const useCacheEndpoint = useSelector(selectDLEnabledForInstrumentDetail);
  const orgId = useSelector(selectAgentOrgID);

  return useCallback(
    (id: number | string, e: MouseEvent, options?: { externalId?: string }) => {
      const { externalId } = options ?? {};
      const routeId =
        externalId && useCacheEndpoint
          ? computeDlHashIdForCgdo(orgId, externalId)
          : String(id);

      toggleSidebarCallback(
        ROUTES_MAP.instrumentsId.path.replace(':id', routeId),
        {
          type: SidebarComponentTypes.INSTRUMENT,
          data: { id, ...(externalId && { externalId }) },
        },
        e,
      );
    },
    [toggleSidebarCallback, useCacheEndpoint, orgId],
  );
};

export const useToggleTransactionSidebar = () => {
  const toggleSidebarCallback = useToggleSidebar();
  return useCallback(
    (id: number | string, e: MouseEvent) =>
      toggleSidebarCallback(
        ROUTES_MAP.dataExplorerTransactionsId.path.replace(':id', String(id)),
        {
          type: SidebarComponentTypes.TRANSACTION,
          data: { id },
        },
        e,
      ),
    [toggleSidebarCallback],
  );
};

export const useToggleActionEventSidebar = () => {
  const toggleSidebarCallback = useToggleSidebar();
  return useCallback(
    (id: number | string, e: MouseEvent) =>
      toggleSidebarCallback(
        ROUTES_MAP.dataExplorerActionEventsId.path.replace(':id', String(id)),
        {
          type: SidebarComponentTypes.ACTION_EVENT,
          data: { id },
        },
        e,
      ),
    [toggleSidebarCallback],
  );
};

export const useToggleAlertSidebar = () => {
  const toggleSidebarCallback = useToggleSidebar();
  return useCallback(
    (id: number, e: MouseEvent) =>
      toggleSidebarCallback(
        ROUTES_MAP.alertsId.path.replace(':id', String(id)),
        {
          type: SidebarComponentTypes.ALERT,
          data: { alert: { id } },
        },
        e,
      ),
    [toggleSidebarCallback],
  );
};

export const useToggleCaseSidebar = () => {
  const toggleSidebarCallback = useToggleSidebar();
  return useCallback(
    (id: number, e: MouseEvent) =>
      toggleSidebarCallback(
        ROUTES_MAP.casesId.path.replace(':id', String(id)),
        {
          type: SidebarComponentTypes.CASE,
          data: { id },
        },
        e,
      ),
    [toggleSidebarCallback],
  );
};

export const useToggleFinCENSARSidebar = () => {
  const toggleSidebarCallback = useToggleSidebar();
  return useCallback(
    (id: number, e: MouseEvent) =>
      toggleSidebarCallback(
        ROUTES_MAP.filingsSarId.path.replace(':id', String(id)),
        {
          type: SidebarComponentTypes.SAR,
          data: { id },
        },
        e,
      ),
    [toggleSidebarCallback],
  );
};

export const useToggleAiAdminTrackingSidebar = () => {
  const toggleAiAdminTrackingCallback = useToggleSidebar();
  return useCallback(
    (trackingData: AIAdminTracking, e: MouseEvent) =>
      toggleAiAdminTrackingCallback(
        undefined,
        {
          type: SidebarComponentTypes.AI_ADMIN,
          data: {
            content: trackingData,
            contentType: AiAdminTypes.AI_ADMIN_TRACKING,
          },
        },
        e,
      ),
    [toggleAiAdminTrackingCallback],
  );
};
